import dayjs from 'dayjs';
import {
  ICheckinCycleDetail,
  ISpecificCheckinsWithResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {
  CycleOccurrence,
  ManagedCycle,
  ManagedSubmission,
  attachCycleEnrichment,
  buildOccurrenceGroups,
  buildPseudoCycles,
  getManagedSubmissionUserId,
  getOccurrenceKey,
  getParticipationStats,
  getSubmissionCycleId,
  getSubmissionRows,
} from '../team-management/team-management.utils';
import {
  CHECKLISTS_ENABLED,
  getVisibleCheckinParams,
} from '../checklist-availability';
import {isGoalsReflectionQuestion} from '../create-checkin-cycle/components/goals-content';
import {
  DEFAULT_PULSE_EMOJIS,
  PULSE_SCALE,
  pulseLabelForValue,
  pulseValuesMatch,
} from '../shared/pulse-scale';

export type TeamFeedQuestionAnswer = {
  id: string;
  kind: 'question';
  prompt: string;
  response: string;
};

export type TeamFeedGoalsAnswer = {
  id: string;
  kind: 'goals';
  prompt: string;
  response: string;
};

export type TeamFeedPulseAnswer = {
  id: string;
  kind: 'pulse';
  prompt: string;
  emoji: string;
  label: string;
  feedback?: string;
};

export type TeamFeedChecklistAnswer = {
  id: string;
  kind: 'checklist';
  prompt: string;
  items: Array<{completed: boolean; text: string}>;
};

export type TeamFeedAnswer =
  | TeamFeedQuestionAnswer
  | TeamFeedGoalsAnswer
  | TeamFeedPulseAnswer
  | TeamFeedChecklistAnswer;

export const getTeamFeedPublicFilter = (
  include: 'user' | 'cycle' | 'cycleId',
) => ({
  include,
  paginate: false,
  visibility: 'public' as const,
});

export const getTeamFeedEligibilityFilter = (userId: string) => ({
  ...getTeamFeedPublicFilter('cycle'),
  user: userId,
});

export const getSafeTeamFeedSearch = (search: string) => {
  const params = new URLSearchParams(search);
  params.delete('cycleId');
  params.delete('send');
  params.delete('comments');
  const safeSearch = params.toString();
  return safeSearch ? `?${safeSearch}` : '';
};

export const getPublicTeamFeedSubmissions = (
  participantResponse: {data?: unknown[]} | false | null | undefined,
  cycleResponse: {data?: unknown[]} | false | null | undefined,
) =>
  attachCycleEnrichment(
    getSubmissionRows(participantResponse),
    getSubmissionRows(cycleResponse),
  ).filter(
    (submission) =>
      typeof submission.cycle === 'object' &&
      submission.cycle.visibility === 'public',
  );

const getParticipantOccurrenceKey = (
  submission: ManagedSubmission,
  workspaceTimezone?: string,
) => {
  const cycleId = getSubmissionCycleId(submission);
  if (!cycleId || typeof submission.cycle !== 'object') return null;

  return `${cycleId}:${getOccurrenceKey(
    submission,
    submission.cycle,
    workspaceTimezone,
  )}`;
};

export const getParticipantScopedPublicTeamFeedSubmissions = (
  participantResponse: {data?: unknown[]} | false | null | undefined,
  cycleResponse: {data?: unknown[]} | false | null | undefined,
  eligibilityResponse: {data?: unknown[]} | false | null | undefined,
  currentUserId: string,
  workspaceTimezone?: string,
) => {
  const publicSubmissions = getPublicTeamFeedSubmissions(
    participantResponse,
    cycleResponse,
  );
  const eligibleOccurrences = new Set(
    getSubmissionRows(eligibilityResponse)
      .filter(
        (submission) =>
          getManagedSubmissionUserId(submission) === currentUserId &&
          typeof submission.cycle === 'object' &&
          submission.cycle.visibility === 'public',
      )
      .map((submission) =>
        getParticipantOccurrenceKey(submission, workspaceTimezone),
      )
      .filter((key): key is string => Boolean(key)),
  );

  return publicSubmissions.filter((submission) => {
    const occurrenceKey = getParticipantOccurrenceKey(
      submission,
      workspaceTimezone,
    );
    return Boolean(occurrenceKey && eligibleOccurrences.has(occurrenceKey));
  });
};

export const getVisiblePublicTeamFeedSubmissions = (
  participantResponse: {data?: unknown[]} | false | null | undefined,
  cycleResponse: {data?: unknown[]} | false | null | undefined,
  eligibilityResponse: {data?: unknown[]} | false | null | undefined,
  currentUserId: string,
  canViewAllPublic: boolean,
  workspaceTimezone?: string,
) =>
  canViewAllPublic
    ? getPublicTeamFeedSubmissions(participantResponse, cycleResponse)
    : getParticipantScopedPublicTeamFeedSubmissions(
        participantResponse,
        cycleResponse,
        eligibilityResponse,
        currentUserId,
        workspaceTimezone,
      );

export const buildPublicTeamFeedCycles = (
  submissions: ManagedSubmission[],
): ManagedCycle[] => buildPseudoCycles(submissions);

const buildQuestionAnswers = (
  detail?: ISpecificCheckinsWithResponseData | null,
): Array<TeamFeedQuestionAnswer | TeamFeedGoalsAnswer> =>
  (detail?.responses?.questions?.questions || [])
    .filter((question) => {
      const response = question?.response;
      return (
        (typeof response === 'string' && response.trim().length > 0) ||
        typeof response === 'number'
      );
    })
    .map((question, index) => {
      const prompt = question.question || 'Question';
      return {
        id: question._id || question.questionId || `question-${index}`,
        kind: isGoalsReflectionQuestion(prompt)
          ? ('goals' as const)
          : ('question' as const),
        prompt,
        response: String(question.response),
      };
    });

const buildChecklistAnswer = (
  detail?: ISpecificCheckinsWithResponseData | null,
  cycle?: ICheckinCycleDetail | null,
): TeamFeedChecklistAnswer[] => {
  const items = (detail?.responses?.priorities?.currentCheckin || [])
    .filter((item) => Boolean(item?.text?.trim()))
    .map((item) => ({
      completed: Boolean(item.completed),
      text: item.text?.trim() || '',
    }));

  if (!items.length) return [];

  return [
    {
      id: detail?.responses?.priorities?.id || 'checklist',
      kind: 'checklist',
      prompt: cycle?.content?.priorities?.[0]?.title || 'Checklist',
      items,
    },
  ];
};

const buildPulseAnswer = (
  detail?: ISpecificCheckinsWithResponseData | null,
  cycle?: ICheckinCycleDetail | null,
): TeamFeedPulseAnswer[] => {
  const pulse = detail?.responses?.pulse;
  if (!pulse?.mood) return [];

  const configuredPulse = cycle?.content?.pulse?.[0];
  const configuredOptions = configuredPulse?.options || [];
  const configuredIndex = configuredOptions.findIndex((option) =>
    pulseValuesMatch(option, pulse.mood),
  );
  const defaultIndex = PULSE_SCALE.findIndex((option) =>
    pulseValuesMatch(option.responseValue, pulse.mood),
  );
  const optionIndex = configuredIndex >= 0 ? configuredIndex : defaultIndex;
  const configuredLabel =
    configuredIndex >= 0 ? configuredOptions[configuredIndex] : '';

  return [
    {
      id: pulse.id || 'pulse',
      kind: 'pulse',
      prompt: configuredPulse?.name || 'Workload pulse',
      emoji:
        configuredPulse?.emojis?.[configuredIndex] ||
        DEFAULT_PULSE_EMOJIS[optionIndex] ||
        '🙂',
      label: pulseLabelForValue(configuredLabel || pulse.mood),
      feedback: pulse.feedback?.trim() || undefined,
    },
  ];
};

export const buildTeamFeedAnswers = (
  detail?: ISpecificCheckinsWithResponseData | null,
  cycle?: ICheckinCycleDetail | null,
): TeamFeedAnswer[] => {
  const answersByType = {
    questions: buildQuestionAnswers(detail),
    priorities: CHECKLISTS_ENABLED ? buildChecklistAnswer(detail, cycle) : [],
    pulse: buildPulseAnswer(detail, cycle),
  };
  const preferredOrder = detail?.params?.length
    ? getVisibleCheckinParams(detail.params)
    : getVisibleCheckinParams(['questions', 'priorities', 'pulse']);
  const orderedTypes = [
    ...preferredOrder,
    ...Object.keys(answersByType).filter(
      (type) => !preferredOrder.includes(type),
    ),
  ];

  return orderedTypes.reduce<TeamFeedAnswer[]>((answers, type) => {
    const typedAnswers =
      answersByType[type as keyof typeof answersByType] || [];
    return [...answers, ...typedAnswers];
  }, []);
};

export const buildTeamFeedOccurrences = (
  submissions: ManagedSubmission[],
  cycle: ManagedCycle,
  workspaceTimezone?: string,
) => buildOccurrenceGroups(submissions, cycle, workspaceTimezone);

export const getTeamFeedParticipation = (occurrence?: CycleOccurrence) =>
  getParticipationStats(occurrence?.submissions || []);

export const getTeamFeedMentionableUserIds = (
  submissions: ManagedSubmission[],
  currentUserId?: string,
) =>
  Array.from(
    new Set(
      submissions
        .map((submission) =>
          typeof submission.user === 'object'
            ? submission.user?.id
            : submission.user,
        )
        .filter(
          (userId): userId is string =>
            Boolean(userId) && userId !== currentUserId,
        ),
    ),
  );

export const formatSendOptionLabel = (
  occurrence: CycleOccurrence,
  isLatest: boolean,
) => {
  const date = dayjs(occurrence.key);
  const label = date.isValid()
    ? date.format('MMM D, YYYY')
    : occurrence.label.replace(/^Sent\s+/i, '');
  return `${label}${isLatest ? ' (latest)' : ''}`;
};
